import { apiClient } from "@/api/api-client";
import { Tiptap } from "@/components/common/tiptap/Tiptap";
import { Button } from "@/components/core/buttons/button";
import Combobox from "@/components/core/forms/combobox";
import { Form, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import { InputFile } from "@/components/core/forms/input-file";
import TagsInput from "@/components/core/forms/tags-input";
import { Textarea } from "@/components/core/forms/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import helpCategoryService from "@/features/dashboard/api/services/help-category-service";
import roleService from "@/features/dashboard/api/services/role-service";
import type { HelpCategory } from "@/features/dashboard/types/help-category";
import { type HelpTopicFormSchema, helpTopicFormSchema } from "@/features/dashboard/types/help-topic";
import type { Role } from "@/features/dashboard/types/role";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/help-topics_/create")({
	component: RouteComponent,
});

function RouteComponent() {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [videoFile, setVideoFile] = useState<File | null>(null);
	const form = useForm<HelpTopicFormSchema>({
		resolver: zodResolver(helpTopicFormSchema),
		defaultValues: {
			title: "",
			body: "",
			description: "",
			video: undefined,
			attachment: undefined,
			roles: [],
		},
	});

	const loadCategoryOptions = async (value: string) => {
		const items = await helpCategoryService.list({
			"filter[search]": value,
		});

		return items.data.map((item: HelpCategory) => ({
			value: item.id,
			label: item.name,
		}));
	};

	const loadRoleOptions = async (value: string) => {
		const items = await roleService.list({
			"filter[search]": value,
		});

		return items.map((item: Role) => ({
			value: item.id,
			label: item.name,
		}));
	};

	const handleSetFile = (file: any) => {
		form.setValue("attachment", URL.createObjectURL(file));
		setVideoFile(file || null);
	};

	const handleRemoveFile = () => {
		setVideoFile(null);
		//add code for removing media
	};

	const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		console.log("onFileChange");
		form.setValue("attachment", null);
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files?.[0] || null;
			setVideoFile(URL.createObjectURL(file));
			if (file) {
				form.setValue("attachment", file);
			}
		}
	};

	const handleSubmit = async (data: HelpTopicFormSchema) => {
		setIsLoading(true);
		const formData = new FormData();
		Object.keys(data).forEach((key) => {
			const typedKey = key as keyof HelpTopicFormSchema;
			if (typedKey === "category") {
				formData.append("help_category_id", data.category ? data.category.value : "");
			} else if (data[typedKey] instanceof File) {
				formData.append(key, data[typedKey] as File);
			} else if (typedKey === "roles") {
				data[typedKey].forEach((item: any) => {
					formData.append("roles[]", item.value);
				});
			} else {
				formData.append(key, data[typedKey] as string);
			}
		});

		try {
			setIsLoading(true);
			const response = await apiClient.post("/help-topics", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			console.log(response);

			toast.success("Help Topic successfully saved.");

			window.location.href = "/help-topics";
		} catch (error) {
			toast.error("Failed to save help topic.");
		}
		setIsLoading(false);
	};

	return (
		<Content pageName="Help Topics">
			{/*<Header title="Help Topics" />*/}
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<Form {...form}>
						<form className="flex flex-col w-full max-w-base p-6 gap-6" onSubmit={(e) => e.preventDefault()}>
							<div className="flex flex-row items-center justify-between w-full gap-4">
								<div className="inline-flex flex-col w-auto">
									<span className="text-xl text-foreground-dark font-semibold">New Help Topic</span>
								</div>
								<div className="inline-flex flex-row w-auto gap-2">
									<Button
										type="submit"
										variant="ghost"
										disabled={isLoading}
										loading={isLoading}
										onClick={() => {
											window.location.href = "/help-topics";
										}}
									>
										Back
									</Button>

									<Button
										variant="default"
										disabled={isLoading}
										loading={isLoading}
										onClick={() => {
											form.trigger();
											handleSubmit(form.getValues());
										}}
									>
										Submit
									</Button>
								</div>
							</div>
							<div className="flex flex-col w-full">
								<Tabs
									defaultValue="editor"
									onValueChange={() => {
										console.log(form.getValues());
									}}
								>
									<TabsList>
										<TabsTrigger value="editor">Editor</TabsTrigger>
										<TabsTrigger value="preview">Preview</TabsTrigger>
									</TabsList>

									<TabsContent value="editor">
										<div className="my-4">
											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
												<div className="col-span-12 w-full">
													<FormField
														control={form.control}
														name="title"
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Title</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup iconPosition="left">
																	<Input type="text" autoComplete="off" {...field} disabled={isLoading} />
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											</div>

											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
												<div className="col-span-12 w-full">
													<FormField
														control={form.control}
														name="description"
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel>Description</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup iconPosition="left">
																	<Textarea {...field} autoComplete="off" disabled={isLoading} value={field.value ?? ""} maxLength={1000} />
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											</div>

											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
												<div className="col-span-12 w-full">
													<FormField
														control={form.control}
														name="help_category_id"
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Categories</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup iconPosition="left">
																	<Combobox
																		defaultValue={field.value}
																		onValueChange={(value) => {
																			field.onChange(value);
																		}}
																		placeholderValue={{
																			label: "-",
																			value: field.value,
																		}}
																		loadOptions={loadCategoryOptions}
																	/>
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											</div>

											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
												<div className="col-span-12 w-full">
													<FormField
														control={form.control}
														name="roles"
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Target Roles</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup iconPosition="left">
																	<TagsInput
																		isMulti={true}
																		defaultValue={field.value}
																		onValueChange={(value) => {
																			field.onChange(value);
																		}}
																		loadOptions={loadRoleOptions}
																	/>
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											</div>

											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
												<div className="col-span-12 w-full">
													<FormField
														control={form.control}
														name="video"
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel>Video</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup iconPosition="left">
																	<InputFile
																		{...field}
																		accept="video/mp4, video/webm"
																		onChange={onFileChange}
																		setFile={handleSetFile}
																		removeUploadedFile={handleRemoveFile}
																		disabled={isLoading}
																		// value={field.value ? (field.value as File).name : undefined}
																	/>
																	{/* <Input type="text" autoComplete="off" {...field} disabled={isLoading} /> */}
																	{/* <InputFile
                                    {...field}
                                    accept="image/png, image/jpeg"
                                    onChange={onImageChange}
                                    setFile={handleSetFile}
                                    removeUploadedFile={
                                      handleRemoveUploadedFile
                                    }
                                    uploadedFile={data?.coverphoto || null}
                                    disabled={isLoading}
                                  /> */}
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											</div>

											{videoFile && (
												<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3 mb-3">
													<div className="col-span-12 w-full">
														<video id="video" className="block w-full" disablePictureInPicture playsInline controls={true}>
															<source src={videoFile} type={videoFile?.type ? videoFile.type : "video/mp4"} />
														</video>
													</div>
												</div>
											)}

											<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
												<div className="col-span-12 w-full">
													<FormField
														control={form.control}
														name="body"
														render={({ field }) => (
															<FormItem>
																<div className="flex flex-col w-full">
																	<FormLabel required>Body</FormLabel>
																	<FormDescription />
																</div>
																<FormGroup iconPosition="left">
																	{/* <Input type="text" autoComplete="off" {...field} /> */}
																	<Tiptap content={field.value} onUpdate={field.onChange} disabled={true} />
																</FormGroup>
																<FormMessage />
															</FormItem>
														)}
													/>
												</div>
											</div>
										</div>
									</TabsContent>

									<TabsContent value="preview">
										<div className="w-full h-full flex flex-col gap-6 mt-10">
											<div className="flex justify-center">
												<div className="w-9/12 gap-3 flex flex-col">
													<div className="flex flex-col">
														<div className="text-3xl font-bold">{form.getValues("title")}</div>
													</div>

													<div className="flex flex-col">
														<div className="">{form.getValues("description")}</div>
													</div>

													{videoFile && (
														<div className="flex flex-col">
															<video className="block w-full" disablePictureInPicture playsInline controls={true}>
																<source src={videoFile} type={videoFile ? videoFile.type : "video/mp4"} />
															</video>
														</div>
													)}

													<div className="font-base text-gray-600 text-pretty">
														<div
															className="text-base text-foreground-dark editor"
															// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
															dangerouslySetInnerHTML={{
																__html: form.getValues("body") || "",
															}}
															key={Date()}
														/>
													</div>
												</div>
											</div>
										</div>
									</TabsContent>
								</Tabs>
							</div>
						</form>
					</Form>
				</div>
			</div>
		</Content>
	);
}
